/**
 * Quick Launch Tab Component
 * Displays pre-configured buttons for expedited trainee evaluations
 * Part of Analysis Results page
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Stack,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Switch,
  FormControlLabel,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import type { AxiosError } from 'axios';
import {
  RocketLaunch as RocketIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Refresh as RefreshIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  School as SchoolIcon,
  Shield as ShieldIcon,
  Repeat as RepeatIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '@signals/app-shell/services/api';
import {
  QuickLaunch,
  QuickLaunchCreate,
  QuickLaunchUpdate,
  ProcessingStatus,
  CoTravelerFilterType,
} from '@signals/app-shell/types/scheduled';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import { useCourseLabels } from '@signals/app-shell/hooks/useCourseLabels';
import { useSettingsStore } from '@signals/app-shell/stores';
import {
  formatLocalDateTime,
  formatWallClockTime,
  getPreferredTimeZone,
  localDatePartsToYmd,
  localTimeInputToHms,
} from '@signals/app-shell/utils/time';

// Course status from courses table (for expiration checking)
interface CourseStatus {
  course_id: string;
  name?: string;
  start_date?: string;
  end_date?: string;
  is_active: boolean;
  status: 'active' | 'expired' | 'upcoming' | 'inactive';
}

interface QuickLaunchTabProps {
  onRefresh?: () => void;
}

const QuickLaunchTab: React.FC<QuickLaunchTabProps> = ({ onRefresh: _onRefresh }) => {
  const { singular, pluralLower } = useParticipantLabels();
  const { courseSingular } = useCourseLabels();
  const queryClient = useQueryClient();
  const preferredTimezone = useSettingsStore((state) => state.settings.temporal.preferredTimezone);
  const scheduleTimezone = getPreferredTimeZone(preferredTimezone);
  
  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedForEdit, setSelectedForEdit] = useState<QuickLaunch | null>(null);
  const [selectedForDelete, setSelectedForDelete] = useState<QuickLaunch | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<Partial<QuickLaunchCreate>>({
    name: '',
    description: '',
    course_id: '',
    students: [],
    exercise_date: localDatePartsToYmd(new Date()),
    exercise_start_time: '08:00:00',
    exercise_end_time: '20:00:00',
    include_cotravelers: false,
    repeat_daily: false,
    schedule_time: '06:00:00',  // Default: 6 AM (2 hours before default exercise start)
  });
  
  // Default co-traveler filter (same options as Pathfinder)
  const [defaultCoTravelerFilter, setDefaultCoTravelerFilter] = useState<CoTravelerFilterType>('all');

  const getCreateErrorMessage = (err: unknown): string => {
    const axiosErr = err as AxiosError<any>;
    const detail = axiosErr?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim().length > 0) return detail;

    const msg = (axiosErr as any)?.message;
    if (typeof msg === 'string' && msg.trim().length > 0) {
      if (msg.toLowerCase().includes('timeout') || msg.toLowerCase().includes('econnaborted')) {
        return 'Create request timed out. The server may be unreachable or overloaded—please try again.';
      }
      return msg;
    }

    return 'Failed to create Quick Launch. Please try again.';
  };

  
  // Fetch Quick Launches
  const { data: quickLaunches, isLoading, error, refetch } = useQuery({
    queryKey: ['quickLaunches'],
    queryFn: () => apiService.getQuickLaunches(),
    refetchInterval: 10000, // Check status every 10 seconds
  });

  // Fetch courses for dropdown (includes students per course)
  const { data: courses } = useQuery({
    queryKey: ['sentinelCourses'],
    queryFn: () => apiService.getSentinelCourses(),
  });

  // Fetch course status info (dates, active status) from the courses table
  const { data: courseStatusResponse } = useQuery({
    queryKey: ['courseStatusData'],
    queryFn: () => apiService.getCourses(true, true), // include expired and inactive
    staleTime: 60000, // Cache for 1 minute
  });
  
  // Extract the courses array from the response
  const courseStatusData: CourseStatus[] | undefined = courseStatusResponse?.courses;

  // Get students from the courses data (courses contains {course_id: [students]})
  const availableStudents = formData.course_id && courses 
    ? courses[formData.course_id] || [] 
    : [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: QuickLaunchCreate) => apiService.createQuickLaunch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quickLaunches'] });
      setCreateDialogOpen(false);
      setCreateError(null);
      resetForm();
    },
    onError: (err) => {
      setCreateError(getCreateErrorMessage(err));
    },
    onSettled: () => {
      // Even on error/timeout, the backend may have created the record; refresh to be safe.
      queryClient.invalidateQueries({ queryKey: ['quickLaunches'] });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteQuickLaunch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quickLaunches'] });
      setDeleteDialogOpen(false);
      setSelectedForDelete(null);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: QuickLaunchUpdate }) => 
      apiService.updateQuickLaunch(id, data),
    onSuccess: () => {
      // Close dialog immediately - don't wait for refetch
      setEditDialogOpen(false);
      setSelectedForEdit(null);
      setEditError(null);
      // Refetch in background (non-blocking)
      queryClient.invalidateQueries({ queryKey: ['quickLaunches'] });
      queryClient.invalidateQueries({ queryKey: ['quickLaunchButtons'] });
    },
    onError: (err) => {
      setEditError(getCreateErrorMessage(err));
    },
    onSettled: () => {
      // Ensure queries are invalidated even on error/timeout (non-blocking)
      queryClient.invalidateQueries({ queryKey: ['quickLaunches'] });
      queryClient.invalidateQueries({ queryKey: ['quickLaunchButtons'] });
    },
  });

  // Open edit dialog with pre-populated data
  const handleOpenEdit = (ql: QuickLaunch) => {
    setSelectedForEdit(ql);
    setFormData({
      name: ql.name,
      description: ql.description || '',
      course_id: ql.course_id || '',
      students: ql.students || [],
      exercise_date: ql.exercise_date?.split('T')[0] || localDatePartsToYmd(new Date()),
      exercise_start_time: ql.exercise_start_time || '08:00:00',
      exercise_end_time: ql.exercise_end_time || '20:00:00',
      include_cotravelers: ql.include_cotravelers || false,
      repeat_daily: ql.repeat_daily || false,
      schedule_time: ql.schedule_time || '06:00:00',
      timezone: ql.timezone || scheduleTimezone,
    });
    setDefaultCoTravelerFilter((ql.cotraveler_filter as CoTravelerFilterType) || 'all');
    setEditError(null);
    setEditDialogOpen(true);
  };

  // Handle edit submit
  const handleEditSubmit = () => {
    if (!selectedForEdit || !formData.name) return;

    setEditError(null);
    
    // schedule_time is wall-clock intent in timezone; backend stores next_run_at as UTC.
    const scheduleTime = formData.repeat_daily && formData.schedule_time
      ? localTimeInputToHms(formData.schedule_time)
      : undefined;
    
    updateMutation.mutate({
      id: selectedForEdit.id,
      data: {
        name: formData.name,
        description: formData.description,
        course_id: formData.course_id,
        students: formData.students,
        exercise_date: formData.exercise_date,
        exercise_start_time: formData.exercise_start_time,
        exercise_end_time: formData.exercise_end_time,
        include_cotravelers: formData.include_cotravelers,
        cotraveler_filter: formData.include_cotravelers ? defaultCoTravelerFilter : undefined,
        repeat_daily: formData.repeat_daily || false,
        schedule_time: scheduleTime,
        timezone: formData.timezone || selectedForEdit.timezone || scheduleTimezone,
      },
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      course_id: '',
      students: [],
      exercise_date: localDatePartsToYmd(new Date()),
      exercise_start_time: '08:00:00',
      exercise_end_time: '20:00:00',
      include_cotravelers: false,
      repeat_daily: false,
      schedule_time: '06:00:00',
    });
    setDefaultCoTravelerFilter('all');
  };

  const handleCreateSubmit = () => {
    if (!formData.name || !formData.course_id || !formData.students?.length) {
      return;
    }

    setCreateError(null);
    
    // schedule_time is wall-clock intent in timezone; backend stores next_run_at as UTC.
    const scheduleTime = formData.repeat_daily && formData.schedule_time
      ? localTimeInputToHms(formData.schedule_time)
      : undefined;
    
    createMutation.mutate({
      name: formData.name!,
      description: formData.description,
      course_id: formData.course_id!,
      students: formData.students!,
      exercise_date: formData.exercise_date!,
      exercise_start_time: formData.exercise_start_time!,
      exercise_end_time: formData.exercise_end_time!,
      include_cotravelers: formData.include_cotravelers!,
      cotraveler_filter: formData.include_cotravelers ? defaultCoTravelerFilter : undefined,
      repeat_daily: formData.repeat_daily || false,
      schedule_time: scheduleTime,
      timezone: scheduleTimezone,
    });
  };

  // Filter to only show DHOST/DHOQC/Trainee courses
  const filteredCourses = courses 
    ? Object.keys(courses).filter(c => 
        c.toUpperCase().includes('DHOST') || 
        c.toUpperCase().includes('DHOQC') ||
        c.toUpperCase().includes('TRAINEE') ||
        c.toUpperCase().includes('STUDENT')
      )
    : [];

  const filteredStudents = availableStudents?.filter((s: string) =>
    s.startsWith('Trainee-') || s.startsWith('Student-') || s.startsWith('DHOST-') || s.startsWith('DHOQC-')
  ) || [];

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={6} lg={4} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={32} />
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="rectangular" height={100} sx={{ mt: 2 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load Quick Launch configurations. Please try again.
      </Alert>
    );
  }

  return (
    <Box sx={{ pl: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RocketIcon color="primary" />
            Quick Launch
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pre-configured buttons for expedited student/trainee evaluations
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreateError(null);
              setCreateDialogOpen(true);
            }}
          >
            Create Quick Launch
          </Button>
        </Stack>
      </Box>

      {/* Empty State */}
      {(!quickLaunches || quickLaunches.length === 0) && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            No Quick Launch configurations yet.
          </Typography>
          <Typography variant="body2">
            Create a Quick Launch to set up one-click buttons that load Pathfinder with 
            pre-configured students, date ranges, and co-travelers for fast student/trainee evaluations.
          </Typography>
        </Alert>
      )}

      {/* Quick Launch Cards */}
      <Grid container spacing={3}>
        {quickLaunches?.map((ql) => (
          <Grid item xs={12} md={6} lg={4} key={ql.id}>
            <QuickLaunchCard
              quickLaunch={ql}
              onEdit={() => handleOpenEdit(ql)}
              onDelete={() => {
                setSelectedForDelete(ql);
                setDeleteDialogOpen(true);
              }}
              courseStatusData={courseStatusData}
            />
          </Grid>
        ))}
      </Grid>

      {/* Create Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RocketIcon color="primary" />
          Create Quick Launch
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {createError && (
              <Alert severity="error" onClose={() => setCreateError(null)}>
                {createError}
              </Alert>
            )}
            <TextField
              label="Name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., DHOST-01 Day 1 Evaluation or DHOQC-26-002 Testing"
              required
            />
            
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />

            <FormControl fullWidth required>
              <InputLabel>{courseSingular}</InputLabel>
              <Select
                value={formData.course_id}
                label={courseSingular}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  course_id: e.target.value,
                  students: [] // Reset students when course changes
                })}
              >
                {filteredCourses.map((course) => (
                  <MenuItem key={course} value={course}>
                    {course}
                  </MenuItem>
                ))}
                {/* Show all courses if no DHOST courses found */}
                {filteredCourses.length === 0 && courses && Object.keys(courses).map((course) => (
                  <MenuItem key={course} value={course}>
                    {course}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.course_id && (
              <FormControl fullWidth required>
                <InputLabel>Select Students/Trainees</InputLabel>
                <Select
                  multiple
                  value={formData.students}
                  label="Select Students/Trainees"
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    students: e.target.value as string[]
                  })}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {filteredStudents.map((student: string) => (
                    <MenuItem key={student} value={student}>
                      <Checkbox checked={formData.students?.includes(student) || false} />
                      {student}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Exercise Window
            </Typography>
            
            <TextField
              label="Exercise Date"
              type="date"
              fullWidth
              value={formData.exercise_date}
              onChange={(e) => setFormData({ ...formData, exercise_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Time"
                type="time"
                fullWidth
                value={formData.exercise_start_time?.slice(0, 5)}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  exercise_start_time: e.target.value + ':00'
                })}
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                label="End Time"
                type="time"
                fullWidth
                value={formData.exercise_end_time?.slice(0, 5)}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  exercise_end_time: e.target.value + ':00'
                })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.repeat_daily || false}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    repeat_daily: e.target.checked,
                    // Default to 2 hours before exercise start when enabling
                    schedule_time: e.target.checked && !formData.schedule_time 
                      ? (() => {
                          const startTime = formData.exercise_start_time || '08:00:00';
                          const [hours] = startTime.split(':').map(Number);
                          const scheduleHour = Math.max(0, hours - 2);
                          return `${scheduleHour.toString().padStart(2, '0')}:00:00`;
                        })()
                      : formData.schedule_time
                  })}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RepeatIcon fontSize="small" color={formData.repeat_daily ? 'primary' : 'disabled'} />
                  <Box>
                    <Typography variant="body1">Repeat Daily</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Auto-update date and re-run co-traveler analysis each day
                    </Typography>
                  </Box>
                </Box>
              }
              sx={{ mt: 2, alignItems: 'flex-start' }}
            />
            
            {formData.repeat_daily && (
              <TextField
                label="Run Analysis At"
                type="time"
                fullWidth
                value={formData.schedule_time?.slice(0, 5) || '06:00'}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  schedule_time: e.target.value + ':00'
                })}
                InputLabelProps={{ shrink: true }}
                helperText="Time when co-traveler analysis runs each day (set before exercise starts)"
                sx={{ mt: 1 }}
              />
            )}

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Co-Traveler Options (same as Pathfinder)
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Load Mode</InputLabel>
              <Select
                value={formData.include_cotravelers ? defaultCoTravelerFilter : 'none'}
                label="Load Mode"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'none') {
                    setFormData({ ...formData, include_cotravelers: false });
                  } else {
                    setFormData({ ...formData, include_cotravelers: true });
                    setDefaultCoTravelerFilter(value as CoTravelerFilterType);
                  }
                }}
              >
                <MenuItem value="none">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon fontSize="small" />
                    <Box>
                      <Typography variant="body2">Load Selected</Typography>
                      <Typography variant="caption" color="text.secondary">Just the selected students/trainees</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.secondary', display: 'block' }}>
                  Co-Traveler Options
                </Typography>
                <MenuItem value="all">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon fontSize="small" color="secondary" />
                    <Box>
                      <Typography variant="body2">All Co-Travelers</Typography>
                      <Typography variant="caption" color="text.secondary">Selected + all co-traveler tracks</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                <MenuItem value="students">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SchoolIcon fontSize="small" sx={{ color: '#4caf50' }} />
                    <Box>
                      <Typography variant="body2">{`${singular} Co-Travelers`}</Typography>
                      <Typography variant="caption" color="text.secondary">{`Selected + ${singular} co-travelers only`}</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                <MenuItem value="rp_srp">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShieldIcon fontSize="small" sx={{ color: '#f44336' }} />
                    <Box>
                      <Typography variant="body2">RP & SRP Co-Travelers</Typography>
                      <Typography variant="caption" color="text.secondary">Selected + Role Player co-travelers only</Typography>
                    </Box>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={createMutation.isPending || !formData.name || !formData.course_id || !formData.students?.length}
            startIcon={createMutation.isPending ? <CircularProgress size={16} /> : <RocketIcon />}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Quick Launch'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Quick Launch?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedForDelete?.name}"? 
            This will remove all pre-computed co-travelers.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => selectedForDelete && deleteMutation.mutate(selectedForDelete.id)}
            disabled={deleteMutation.isPending}
            startIcon={deleteMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          Edit Quick Launch
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {editError && (
              <Alert severity="error" onClose={() => setEditError(null)}>
                {editError}
              </Alert>
            )}

            {/* Warning if original course is expired or inactive */}
            {selectedForEdit?.course_id && courseStatusData && (() => {
              const courseInfo = courseStatusData.find((c: CourseStatus) => c.course_id === selectedForEdit.course_id);
              if (courseInfo?.status === 'expired') {
                return (
                  <Alert severity="error" icon={<WarningIcon />}>
                    <Typography variant="body2">
                      <strong>{courseSingular} expired:</strong> The {courseSingular.toLowerCase()} &quot;{selectedForEdit.course_id}&quot; has expired
                      {courseInfo?.end_date ? ` (ended ${courseInfo.end_date})` : ''}. 
                      {`Please select a current ${courseSingular.toLowerCase()}.`}
                    </Typography>
                  </Alert>
                );
              } else if (courseInfo?.is_active === false) {
                return (
                  <Alert severity="warning" icon={<WarningIcon />}>
                    <Typography variant="body2">
                      <strong>{courseSingular} inactive:</strong> The {courseSingular.toLowerCase()} &quot;{selectedForEdit.course_id}&quot; is currently inactive. 
                      {`Please select an active ${courseSingular.toLowerCase()}.`}
                    </Typography>
                  </Alert>
                );
              }
              return null;
            })()}

            <TextField
              label="Name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />

            <FormControl fullWidth required>
              <InputLabel>{courseSingular}</InputLabel>
              <Select
                value={formData.course_id}
                label={courseSingular}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  course_id: e.target.value,
                  students: [] // Reset students when course changes
                })}
              >
                {filteredCourses.map((course) => (
                  <MenuItem key={course} value={course}>
                    {course}
                  </MenuItem>
                ))}
                {filteredCourses.length === 0 && courses && Object.keys(courses).map((course) => (
                  <MenuItem key={course} value={course}>
                    {course}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.course_id && (
              <FormControl fullWidth required>
                <InputLabel>Select Students/Trainees</InputLabel>
                <Select
                  multiple
                  value={formData.students}
                  label="Select Students/Trainees"
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    students: e.target.value as string[]
                  })}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {filteredStudents.map((student: string) => (
                    <MenuItem key={student} value={student}>
                      <Checkbox checked={formData.students?.includes(student) || false} />
                      {student}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Exercise Window
            </Typography>
            
            <TextField
              label="Exercise Date"
              type="date"
              fullWidth
              value={formData.exercise_date}
              onChange={(e) => setFormData({ ...formData, exercise_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Time"
                type="time"
                fullWidth
                value={formData.exercise_start_time?.slice(0, 5)}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  exercise_start_time: e.target.value + ':00'
                })}
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                label="End Time"
                type="time"
                fullWidth
                value={formData.exercise_end_time?.slice(0, 5)}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  exercise_end_time: e.target.value + ':00'
                })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.repeat_daily || false}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    repeat_daily: e.target.checked,
                    schedule_time: e.target.checked && !formData.schedule_time 
                      ? (() => {
                          const startTime = formData.exercise_start_time || '08:00:00';
                          const [hours] = startTime.split(':').map(Number);
                          const scheduleHour = Math.max(0, hours - 2);
                          return `${scheduleHour.toString().padStart(2, '0')}:00:00`;
                        })()
                      : formData.schedule_time
                  })}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RepeatIcon fontSize="small" color={formData.repeat_daily ? 'primary' : 'disabled'} />
                  <Box>
                    <Typography variant="body1">Repeat Daily</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Auto-update date and re-run co-traveler analysis each day
                    </Typography>
                  </Box>
                </Box>
              }
              sx={{ mt: 2, alignItems: 'flex-start' }}
            />
            
            {formData.repeat_daily && (
              <TextField
                label="Run Analysis At"
                type="time"
                fullWidth
                value={formData.schedule_time?.slice(0, 5) || '06:00'}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  schedule_time: e.target.value + ':00'
                })}
                InputLabelProps={{ shrink: true }}
                helperText="Time when co-traveler analysis runs each day"
                sx={{ mt: 1 }}
              />
            )}

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Co-Traveler Options
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Load Mode</InputLabel>
              <Select
                value={formData.include_cotravelers ? defaultCoTravelerFilter : 'none'}
                label="Load Mode"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'none') {
                    setFormData({ ...formData, include_cotravelers: false });
                  } else {
                    setFormData({ ...formData, include_cotravelers: true });
                    setDefaultCoTravelerFilter(value as CoTravelerFilterType);
                  }
                }}
              >
                <MenuItem value="none">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon fontSize="small" />
                    <Box>
                      <Typography variant="body2">Load Selected</Typography>
                      <Typography variant="caption" color="text.secondary">Just the selected students/trainees</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem value="all">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon fontSize="small" color="secondary" />
                    <Box>
                      <Typography variant="body2">All Co-Travelers</Typography>
                      <Typography variant="caption" color="text.secondary">Selected + all co-traveler tracks</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                <MenuItem value="students">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SchoolIcon fontSize="small" sx={{ color: '#4caf50' }} />
                    <Box>
                      <Typography variant="body2">{`${singular} Co-Travelers`}</Typography>
                      <Typography variant="caption" color="text.secondary">{`Selected + ${singular} co-travelers only`}</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                <MenuItem value="rp_srp">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShieldIcon fontSize="small" sx={{ color: '#f44336' }} />
                    <Box>
                      <Typography variant="body2">RP & SRP Co-Travelers</Typography>
                      <Typography variant="caption" color="text.secondary">Selected + Role Player co-travelers only</Typography>
                    </Box>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {formData.include_cotravelers && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {`Changing ${pluralLower}, date, or time window will re-process co-travelers.`}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={updateMutation.isPending || !formData.name}
            startIcon={updateMutation.isPending ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Individual Quick Launch Card Component
interface QuickLaunchCardProps {
  quickLaunch: QuickLaunch;
  onEdit: () => void;
  onDelete: () => void;
  courseStatusData?: CourseStatus[]; // Course date/status info
}

const QuickLaunchCard: React.FC<QuickLaunchCardProps> = ({ quickLaunch, onEdit, onDelete, courseStatusData }) => {
  const { singular } = useParticipantLabels();
  const { courseSingular } = useCourseLabels();
  // Check if the course is expired or inactive based on the courses table
  const courseStatus = courseStatusData?.find(c => c.course_id === quickLaunch.course_id);
  const isCourseExpired = courseStatus?.status === 'expired';
  const isCourseInactive = courseStatus?.is_active === false;
  const courseWarning = isCourseExpired ? 'expired' : isCourseInactive ? 'inactive' : null;
  const navigate = useNavigate();
  
  // Menu anchor state for each student button
  const [menuAnchors, setMenuAnchors] = useState<Record<string, HTMLElement | null>>({});
  
  // Fetch buttons for this Quick Launch
  // Always fetch buttons - they can load without co-travelers even if processing is pending
  const { data: buttons, isLoading } = useQuery({
    queryKey: ['quickLaunchButtons', quickLaunch.id],
    queryFn: () => apiService.getQuickLaunchButtons(quickLaunch.id),
    staleTime: 0, // Always consider data stale so it refetches on invalidation
    refetchOnMount: 'always', // Always refetch when component mounts
  });

  // Extended filter type that includes 'none' for no co-travelers
  type ExtendedFilterType = 'none' | CoTravelerFilterType;
  
  // Get the default filter from the Quick Launch config
  const defaultFilter: ExtendedFilterType = quickLaunch.include_cotravelers 
    ? (quickLaunch.cotraveler_filter || 'all') 
    : 'none';
  
  // Helper function to filter co-travelers by type
  const filterCoTravelers = (cotravelers: string[], filterType: ExtendedFilterType): string[] => {
    if (filterType === 'none') return [];
    if (filterType === 'all') return cotravelers;
    
    return cotravelers.filter(ct => {
      const isStudent = ct.startsWith('Trainee-') || ct.startsWith('Student-') || ct.startsWith('DHOST-') || ct.startsWith('DHOQC-');
      if (filterType === 'students') return isStudent;
      if (filterType === 'rp_srp') return !isStudent; // RP, SRP, etc.
      return true;
    });
  };

  // Build URL with filtered co-travelers
  const buildPathfinderUrl = (studentId: string, cotravelers: string[], filterType: ExtendedFilterType): string => {
    const filteredCoTravelers = filterCoTravelers(cotravelers, filterType);
    const urlParams = new URLSearchParams();
    
    urlParams.set('student', studentId);  // Singular - matches PathfinderPage param
    urlParams.set('course', quickLaunch.course_id || '');
    
    if (quickLaunch.exercise_date) {
      urlParams.set('date', quickLaunch.exercise_date);
    }
    if (quickLaunch.exercise_start_time) {
      urlParams.set('start', quickLaunch.exercise_start_time.substring(0, 5));
    }
    if (quickLaunch.exercise_end_time) {
      urlParams.set('end', quickLaunch.exercise_end_time.substring(0, 5));
    }
    if (quickLaunch.timezone) {
      urlParams.set('timezone', quickLaunch.timezone);
    }
    if (buttons?.start_utc && buttons?.end_utc) {
      urlParams.set('startUtc', buttons.start_utc);
      urlParams.set('endUtc', buttons.end_utc);
    }
    if (filteredCoTravelers.length > 0) {
      urlParams.set('coTravelers', filteredCoTravelers.join(','));
    }
    
    return `/pathfinder?${urlParams.toString()}`;
  };

  // Handle menu open
  const handleMenuOpen = (studentId: string, event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchors(prev => ({ ...prev, [studentId]: event.currentTarget }));
  };

  // Handle menu close
  const handleMenuClose = (studentId: string) => {
    setMenuAnchors(prev => ({ ...prev, [studentId]: null }));
  };

  // Handle load with co-traveler filter
  const handleLoad = (studentId: string, cotravelers: string[], filterType: ExtendedFilterType) => {
    handleMenuClose(studentId);
    const url = buildPathfinderUrl(studentId, cotravelers, filterType);
    navigate(url);
  };

  const getStatusChip = () => {
    const status = quickLaunch.processing_status;
    let color: "success" | "warning" | "error" | "default" = 'default';
    let label: string = status || 'Unknown';
    
    switch (status) {
      case ProcessingStatus.READY:
        color = 'success';
        label = 'Ready';
        break;
      case ProcessingStatus.PROCESSING:
        color = 'warning';
        label = 'Processing Co-Travelers...';
        break;
      case ProcessingStatus.PENDING:
        color = 'warning';
        label = 'Pending';
        break;
      case ProcessingStatus.FAILED:
        color = 'error';
        label = 'Failed';
        break;
      case ProcessingStatus.SKIPPED:
        color = 'default';
        label = 'Skipped';
        break;
    }
    
    return <Chip size="small" color={color} label={label} />;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    // Parse date parts manually to avoid timezone issues
    // Date string format: "2026-01-06" or "2026-01-06T00:00:00"
    const [datePart] = dateStr.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    // Create date using local timezone (month is 0-indexed)
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr?: string) => {
    return formatWallClockTime(timeStr);
  };

  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      border: '1px solid',
      borderColor: quickLaunch.processing_status === ProcessingStatus.READY ? 'success.main' : 'divider',
      position: 'relative', // For positioning the status badge
    }}>
      {/* Status Badge - Bottom Left Corner */}
      <Box sx={{ position: 'absolute', bottom: 16, left: 16, zIndex: 1 }}>
        {getStatusChip()}
      </Box>

      <CardContent sx={{ flex: 1 }}>
        {/* Course Warning (Expired or Inactive) */}
        {courseWarning && (
          <Alert 
            severity={courseWarning === 'expired' ? 'error' : 'warning'} 
            sx={{ mb: 2, py: 0.5 }}
            icon={<WarningIcon fontSize="small" />}
          >
            <Typography variant="body2">
              {courseWarning === 'expired' ? (
                <>{courseSingular} <strong>{quickLaunch.course_id}</strong> has expired{courseStatus?.end_date ? ` (ended ${courseStatus.end_date})` : ''}. Data may not load correctly.</>
              ) : (
                <>{courseSingular} <strong>{quickLaunch.course_id}</strong> is inactive. Please edit and select an active {courseSingular.toLowerCase()}.</>
              )}
            </Typography>
          </Alert>
        )}

        {/* Header with Title and Details */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {/* Left side: Title and Details */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
              {quickLaunch.name}
            </Typography>

            {quickLaunch.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {quickLaunch.description}
              </Typography>
            )}

            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {formatDate(quickLaunch.exercise_date)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimeIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {formatTime(quickLaunch.exercise_start_time)} - {formatTime(quickLaunch.exercise_end_time)}
                </Typography>
              </Box>
              {quickLaunch.include_cotravelers && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {quickLaunch.cotraveler_filter === 'students' ? (
                    <SchoolIcon fontSize="small" sx={{ color: '#4caf50' }} />
                  ) : quickLaunch.cotraveler_filter === 'rp_srp' ? (
                    <ShieldIcon fontSize="small" sx={{ color: '#f44336' }} />
                  ) : (
                    <GroupIcon fontSize="small" color="secondary" />
                  )}
                  <Typography variant="body2">
                    {quickLaunch.cotraveler_filter === 'students' 
                      ? `${singular} Co-Travelers` 
                      : quickLaunch.cotraveler_filter === 'rp_srp'
                        ? 'RP & SRP Co-Travelers'
                        : 'All Co-Travelers'}
                  </Typography>
                </Box>
              )}
              {quickLaunch.repeat_daily && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RepeatIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="primary">
                    Repeats Daily {quickLaunch.schedule_time ? `at ${formatTime(quickLaunch.schedule_time)}` : ''}
                  </Typography>
                </Box>
              )}
              {quickLaunch.processing_completed_at && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    Last ran: {formatLocalDateTime(quickLaunch.processing_completed_at, {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Vertical Divider */}
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* Right side: Student Buttons (Stacked) */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'flex-end',
            gap: 1.5,
            minWidth: 140
          }}>
            {/* Student Buttons stacked vertically */}
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'right' }}>
                Students/Trainees ({quickLaunch.students?.length || 0})
              </Typography>

              {/* Student Buttons with Co-Traveler Dropdown */}
              {isLoading ? (
                <Stack spacing={0.5}>
                  {[1, 2].map((i) => (
                    <Skeleton key={i} variant="rectangular" width="100%" height={36} sx={{ borderRadius: 1 }} />
                  ))}
                </Stack>
              ) : buttons ? (
                <Stack spacing={0.5}>
                  {buttons.students.map((student) => {
                    // Determine if student has no data (point_count === 0, not null/undefined)
                    const hasNoData = student.point_count === 0;
                    const tooltipText = hasNoData 
                      ? 'No GPS data found for this student/trainee on this date'
                      : student.cotraveler_count > 0 
                        ? `${student.cotraveler_count} co-traveler(s) - Click to load with ${defaultFilter === 'none' ? 'no' : defaultFilter} co-travelers`
                        : 'No co-travelers detected';
                    
                    return (
                    <Box key={student.student_id} sx={{ width: '100%' }}>
                      <Tooltip title={tooltipText}>
                        <ButtonGroup 
                          variant="contained" 
                          size="small"
                          sx={{ 
                            width: '100%',
                            '& .MuiButton-root': { textTransform: 'none' },
                            // Grey out button if no data
                            ...(hasNoData && {
                              '& .MuiButton-root': { 
                                textTransform: 'none',
                                bgcolor: 'grey.600',
                                '&:hover': { bgcolor: 'grey.700' }
                              }
                            })
                          }}
                        >
                          <Button
                            onClick={() => handleLoad(student.student_id, student.cotravelers, defaultFilter)}
                            sx={{ position: 'relative', flex: 1 }}
                          >
                            {student.student_id}
                            {hasNoData ? (
                              <Chip
                                size="small"
                                label="No data"
                                sx={{ 
                                  ml: 1, 
                                  height: 18, 
                                  fontSize: '0.65rem',
                                  bgcolor: 'rgba(255, 152, 0, 0.3)',
                                  color: '#ff9800',
                                  '& .MuiChip-label': { px: 0.5 }
                                }}
                              />
                            ) : student.cotraveler_count > 0 && (
                              <Chip
                                size="small"
                                label={student.cotraveler_count}
                                color="secondary"
                                sx={{ 
                                  ml: 1, 
                                  height: 18, 
                                  fontSize: '0.7rem',
                                  '& .MuiChip-label': { px: 0.5 }
                                }}
                              />
                            )}
                          </Button>
                          <Button
                            size="small"
                            onClick={(e) => handleMenuOpen(student.student_id, e)}
                            sx={{ px: 0.5, minWidth: 'auto' }}
                          >
                            <ArrowDropDownIcon fontSize="small" />
                          </Button>
                        </ButtonGroup>
                      </Tooltip>

                      {/* Co-Traveler Options Menu - Simplified based on what was configured */}
                      <Menu
                        anchorEl={menuAnchors[student.student_id]}
                        open={Boolean(menuAnchors[student.student_id])}
                        onClose={() => handleMenuClose(student.student_id)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      >
                        {/* Always show "Just this trainee" option */}
                        <MenuItem onClick={() => handleLoad(student.student_id, student.cotravelers, 'none')}>
                          <ListItemIcon>
                            <PeopleIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary="Just Student/Trainee" 
                            secondary="Load without co-travelers"
                          />
                        </MenuItem>
                        
                        {/* Show co-traveler option ONLY if there are co-travelers */}
                        {student.cotraveler_count > 0 && (
                          <>
                            <Divider sx={{ my: 0.5 }} />
                            
                            {/* Show the configured option with checkmark */}
                            <MenuItem 
                              onClick={() => handleLoad(student.student_id, student.cotravelers, defaultFilter === 'none' ? 'all' : defaultFilter)}
                              sx={{ bgcolor: 'action.selected' }}
                            >
                              <ListItemIcon>
                                {defaultFilter === 'rp_srp' ? (
                                  <ShieldIcon fontSize="small" sx={{ color: '#f44336' }} />
                                ) : defaultFilter === 'students' ? (
                                  <SchoolIcon fontSize="small" sx={{ color: '#4caf50' }} />
                                ) : (
                                  <GroupIcon fontSize="small" color="secondary" />
                                )}
                              </ListItemIcon>
                              <ListItemText 
                                primary={
                                  defaultFilter === 'rp_srp' ? 'With RP/SRP Co-Travelers' :
                                  defaultFilter === 'students' ? `With ${singular} Co-Travelers` :
                                  'With All Co-Travelers'
                                }
                                secondary={`Student/Trainee + ${student.cotraveler_count} pre-computed co-travelers`}
                              />
                              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main', ml: 1 }} />
                            </MenuItem>
                          </>
                        )}
                      </Menu>
                    </Box>
                  );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {quickLaunch.processing_status === ProcessingStatus.PROCESSING 
                    ? 'Computing co-travelers...'
                    : 'Unable to load buttons'
                  }
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end', borderTop: '1px solid', borderColor: 'divider' }}>
        <Tooltip title="Edit Quick Launch">
          <IconButton size="small" color="primary" onClick={onEdit}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete Quick Launch">
          <IconButton size="small" color="error" onClick={onDelete}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

export default QuickLaunchTab;

